import React from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import {
  BookOpen,
  ScrollText,
  KeyRound,
  Building2,
  UserCheck,
  Calendar,
  Users,
  Upload,
  Layers,
  Vote,
  ArrowUpRight,
  ShieldCheck,
  Trophy,
  Scale,
  Gift,
  Star,
  Sparkles,
  FileText,
  BadgeCheck,
  AlertTriangle,
  WifiOff,
  RefreshCw,
  Lock,
  Gavel,
  Mail,
  Heart,
} from "lucide-react";
import LegalShell from "./LegalShell";

function Section({ icon: Icon, title, children }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-zinc-900/60 p-6 sm:p-8">
      <h2 className="mb-6 flex items-center gap-2 text-xl font-bold sm:text-2xl">
        <Icon className="h-6 w-6 shrink-0 text-yellow-500" />
        {title}
      </h2>
      <div className="space-y-4 text-zinc-300">{children}</div>
    </div>
  );
}

function PartHeader({ part, title, intro }) {
  return (
    <div className="border-b border-white/10 pb-4 pt-4">
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-yellow-500">
        {part}
      </p>
      <h2 className="mt-2 text-2xl font-bold text-white sm:text-3xl">{title}</h2>
      {intro ? <p className="mt-3 text-zinc-400">{intro}</p> : null}
    </div>
  );
}

function P({ children }) {
  return <p className="leading-relaxed">{children}</p>;
}

function Ul({ items }) {
  return (
    <ul className="list-disc space-y-2 pl-6">
      {items.map((item) => (
        <li key={typeof item === "string" ? item : item.key}>{item}</li>
      ))}
    </ul>
  );
}

const DEFINITIONS = [
  {
    term: "Contest",
    meaning:
      "means the Pagent Girl Group Contest governed by this Official Contest Rulebook.",
  },
  {
    term: "Contest Period",
    meaning:
      "means the period beginning on the Contest Start Date and ending on the completion of the Contest, unless extended, modified, suspended, or terminated in accordance with this Rulebook.",
  },
  {
    term: "Entrant",
    meaning:
      "means an individual who has submitted a valid entry into the Contest.",
  },
  {
    term: "Final Mission",
    meaning:
      "means Mission 5 of the Contest, or any replacement final mission announced by Pagent.",
  },
  {
    term: "Finalist",
    meaning:
      "means an Entrant who has successfully advanced through Mission 4, completed Finalist Verification, and has been confirmed by Pagent to participate in the Final Mission.",
  },
  {
    term: "Finalist Verification",
    meaning:
      "means the process described in this Rulebook through which Pagent confirms a Finalist's eligibility before participation in the Final Mission.",
  },
  {
    term: "Mission",
    meaning:
      "means one of the five stages of the Contest, each consisting of a creative challenge, submission period, and voting period.",
  },
  {
    term: "Pagent, we, our, and us",
    meaning:
      "mean Pagent Social Corp., the sponsor and administrator of the Contest.",
  },
  {
    term: "Pagent App",
    meaning:
      "means the official Pagent mobile application through which the Contest is conducted.",
  },
  {
    term: "Pagent Girl Group",
    meaning:
      "means the five Winners of the Vocalist, Rapper, Dancer, Visual, and Leader & Personality Roles.",
  },
  {
    term: "Role",
    meaning:
      "means one of the categories in which Entrants may compete, namely Vocalist, Rapper, Dancer, Visual, Leader & Personality, or Animal Mascot.",
  },
  {
    term: "Rulebook",
    meaning:
      "means this Official Contest Rulebook, as amended from time to time.",
  },
  {
    term: "Submission",
    meaning:
      "means any video, photograph, audio recording, text, image, caption, username, profile information, or other content submitted by an Entrant in connection with the Contest.",
  },
  {
    term: "Winner",
    meaning:
      "means a Finalist who has successfully completed Winner Confirmation and has been officially declared the winner of a Role by Pagent.",
  },
  {
    term: "Winner Confirmation",
    meaning:
      "means the process through which Pagent verifies a potential Winner's eligibility, confirms the integrity of the applicable voting results, and officially declares that individual a Winner.",
  },
];

const PRINCIPLES = [
  {
    title: "Participation is free.",
    body: "There is no purchase, payment, subscription, or donation required to enter or compete.",
  },
  {
    title: "Anyone can enter.",
    body: "If you meet the eligibility requirements, you may enter by submitting content through the Pagent app.",
  },
  {
    title: "Advancement is earned.",
    body: "Entrants advance through each Mission based on votes received from the Pagent community.",
  },
  {
    title: "There is no random selection.",
    body: "No lottery, raffle, random draw, or similar process is used to determine who advances or wins.",
  },
  {
    title: "Every Mission is different.",
    body: "Each Mission has its own challenge and advancement threshold. Before voting begins for a Mission, Pagent will announce how many Entrants (or what percentage of Entrants) will advance.",
  },
  {
    title: "Winners receive guaranteed prizes.",
    body: "Each Contest winner is guaranteed a minimum cash prize of USD $100, together with official recognition on the Pagent platform.",
  },
  {
    title: "Future opportunities are possible.",
    body: "Pagent hopes the Contest creates exciting opportunities beyond the Contest itself. If interest exists from both the winners and industry partners, Pagent may explore additional music industry opportunities. As a growing company, Pagent cannot guarantee the scale of these opportunities, and they would always be subject to separate discussions and agreements.",
  },
  {
    title: "If you're under the age of majority...",
    body: "You're welcome to compete! However, if you qualify for the Final Mission, we'll ask your parent or legal guardian to complete a verification process before you continue.",
  },
];

const MISSION_SCHEDULE = [
  { mission: "Mission 1", timing: "[Available XX, 2027]" },
  { mission: "Mission 2", timing: "[Available XX, 2027]" },
  { mission: "Mission 3", timing: "[Available January 31, 2027]" },
  { mission: "Mission 4", timing: "[Available, April 30, 2027]" },
  { mission: "Mission 5 (Final Mission)", timing: "[June 30, 2027]" },
];

const ROLES = [
  "Vocalist",
  "Rapper",
  "Dancer",
  "Visual",
  "Leader & Personality",
  "Animal Mascot",
];

export default function GirlGroupRulebook() {
  return (
    <>
      <Helmet>
        <title>Official Contest Rulebook - Pagent Girl Group Contest</title>
        <meta
          name="description"
          content="Official Contest Rulebook for the Pagent Girl Group Contest. Version 1.0. Effective July 23, 2026."
        />
        <link
          rel="canonical"
          href="https://www.pagent.tv/girlgroup/rulebook"
        />
      </Helmet>

      <LegalShell
        title="Official Contest Rulebook"
        subtitle="Pagent Girl Group Contest · Version 1.0 · Effective July 23, 2026"
      >
        <div className="mb-8 text-center">
          <Link
            to="/girlgroup"
            className="inline-flex items-center gap-2 text-sm font-semibold text-yellow-400 transition-colors hover:text-yellow-300"
          >
            ← Back to Girl Group Contest
          </Link>
        </div>

        <div className="prose prose-invert max-w-none">
          <div className="space-y-8">
            {/* Meta */}
            <div className="rounded-2xl border border-yellow-500/30 bg-zinc-900/60 p-6 sm:p-8">
              <div className="mb-4 flex items-center gap-2">
                <BookOpen className="h-6 w-6 text-yellow-500" />
                <h2 className="text-xl font-bold text-white sm:text-2xl">
                  Contest Details
                </h2>
              </div>
              <dl className="grid gap-3 text-sm sm:grid-cols-2 sm:text-base">
                <div>
                  <dt className="font-semibold text-white/50">Version</dt>
                  <dd className="text-zinc-300">1.0</dd>
                </div>
                <div>
                  <dt className="font-semibold text-white/50">Effective Date</dt>
                  <dd className="text-zinc-300">July 23, 2026</dd>
                </div>
                <div>
                  <dt className="font-semibold text-white/50">
                    Contest Period
                  </dt>
                  <dd className="text-zinc-300">
                    July 1, 2026 to approximately September 2027
                  </dd>
                </div>
                <div>
                  <dt className="font-semibold text-white/50">Sponsor</dt>
                  <dd className="text-zinc-300">Pagent Social Corp.</dd>
                </div>
              </dl>
            </div>

            {/* Welcome */}
            <Section icon={ScrollText} title="Welcome">
              <P>
                Welcome to the Pagent Girl Group Contest (the
                &quot;Contest&quot;).
              </P>
              <P>
                The Contest is a global, multi-stage talent contest
                hosted exclusively through the Pagent mobile application
                (&quot;Pagent&quot;). Entrants compete by completing a series of
                creative Missions, submitting original content, and advancing
                through the Contest based on votes cast by members of the
                Pagent community.
              </P>
              <P>
                The purpose of the Contest is to discover talented creators,
                celebrate creativity, and build an engaged global community. The
                Contest is designed to reward creativity, performance,
                personality, and audience engagement.
              </P>
              <P>
                Unlike a giveaway or lottery, advancement and winners are
                determined through public voting. There are no random draws or
                games of chance used to determine who advances or who wins.
              </P>
              <P>
                By participating in the Contest, you agree to follow this
                Official Contest Rulebook (the &quot;Rulebook&quot;),
                together with Pagent&apos;s{" "}
                <Link to="/terms" className="text-yellow-400 hover:underline">
                  Terms of Service
                </Link>{" "}
                and{" "}
                <Link to="/privacy" className="text-yellow-400 hover:underline">
                  Privacy Policy
                </Link>
                .
              </P>
            </Section>

            {/* Definitions */}
            <Section icon={BookOpen} title="Definitions">
              <P>
                Unless the context requires otherwise, the following terms have
                the meanings set out below:
              </P>
              <dl className="space-y-4">
                {DEFINITIONS.map((def) => (
                  <div key={def.term}>
                    <dt className="font-semibold text-white">{def.term}</dt>
                    <dd className="mt-1 text-zinc-300">{def.meaning}</dd>
                  </div>
                ))}
              </dl>
            </Section>

            {/* Key Principles */}
            <Section icon={KeyRound} title="Key Contest Principles">
              <P>Before you begin, here are the most important things to know.</P>
              <div className="space-y-5">
                {PRINCIPLES.map((item) => (
                  <div key={item.title}>
                    <h3 className="font-semibold text-white">{item.title}</h3>
                    <p className="mt-1 leading-relaxed text-zinc-300">
                      {item.body}
                    </p>
                  </div>
                ))}
              </div>
            </Section>

            <PartHeader
              part="Part I"
              title="Contest Overview"
            />

            <Section icon={Building2} title="1. Sponsor">
              <P>
                The Contest is sponsored and administered solely by:
              </P>
              <P>
                <strong className="text-white">Pagent Social Corp.</strong>
              </P>
              <P>
                Throughout this Rulebook, &quot;Pagent,&quot; &quot;we,&quot;
                &quot;our,&quot; and &quot;us&quot; refer to Pagent Social Corp.
              </P>
              <P>
                The Contest is not sponsored, endorsed, administered by, or
                associated with Apple Inc., Google LLC, TikTok, Meta Platforms,
                or any record label unless expressly stated otherwise by Pagent.
              </P>
            </Section>

            <Section icon={UserCheck} title="2. Eligibility">
              <P>To compete, you must:</P>
              <Ul
                items={[
                  "be at least thirteen (13) years old at the time you enter;",
                  "have an active Pagent account in good standing;",
                  "reside in a jurisdiction where participation in the Contest is lawful and where the Pagent app is officially available for download; and",
                  "comply with this Rulebook, the Pagent Terms of Service, and any Contest instructions published by Pagent.",
                ]}
              />
              <P>
                Entrants who have not reached the age of majority in their
                jurisdiction of residence must have the permission of a parent
                or legal guardian to participate in the Contest.
              </P>
              <P>
                Before participating in the Final Mission, any Finalist who has
                not reached the age of majority in their jurisdiction of
                residence must successfully complete Pagent&apos;s parental or
                legal guardian verification process in accordance with this
                Rulebook.
              </P>
              <P>
                Employees, officers, directors, contractors, and representatives
                of Pagent, together with members of their immediate families and
                households, are not eligible to participate unless expressly
                approved by Pagent.
              </P>
              <P>
                The Contest is void where prohibited or restricted by
                applicable law.
              </P>
              <P>
                It is each Entrant&apos;s responsibility to ensure that
                participation in the Contest is lawful in their jurisdiction
                of residence.
              </P>
              <P>
                Pagent reserves the right to restrict or refuse participation
                where reasonably necessary to comply with applicable laws,
                regulations, sanctions, export controls, app store requirements,
                platform policies, or other legal or regulatory obligations.
              </P>
              <P>
                Where local laws prohibit the awarding of a prize or otherwise
                restrict participation in the Contest, Pagent may declare an
                Entrant ineligible or, where permitted by applicable law,
                substitute an alternative prize or form of recognition.
              </P>
              <P>
                Pagent reserves the right to determine an Entrant&apos;s
                eligibility at any time and to require reasonable information or
                documentation to verify eligibility.
              </P>
            </Section>

            <Section icon={Calendar} title="3. Contest Period">
              <P>The Contest begins on [XX].</P>
              <P>
                The Contest is currently expected to conclude on or around
                [September 5, 2027].
              </P>
              <P>The anticipated Contest schedule is:</P>
              <div className="overflow-x-auto rounded-xl border border-white/10">
                <table className="w-full min-w-[320px] text-left text-sm">
                  <thead className="bg-white/5 text-white">
                    <tr>
                      <th className="px-4 py-3 font-semibold">Mission</th>
                      <th className="px-4 py-3 font-semibold">
                        Anticipated Timing
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {MISSION_SCHEDULE.map((row) => (
                      <tr
                        key={row.mission}
                        className="border-t border-white/10"
                      >
                        <td className="px-4 py-3 text-white">{row.mission}</td>
                        <td className="px-4 py-3 text-zinc-300">{row.timing}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <P>These dates are estimates only.</P>
              <P>
                Pagent may reasonably modify the Contest schedule where
                necessary to account for participation levels, operational
                requirements, technical issues, platform development, or other
                circumstances affecting the fair administration of the
                Contest.
              </P>
              <P>
                If changes are made, Pagent will communicate updated timing
                through the Pagent app or other official Pagent communication
                channels.
              </P>
              <P>
                Entrants are responsible for monitoring those communications
                throughout the Contest.
              </P>
            </Section>

            <PartHeader
              part="Part II"
              title="Entering the Contest"
              intro="Every great Contest starts with a single submission. This Part explains how to enter, the different Roles available, and how the Contest progresses from Mission to Mission."
            />

            <Section icon={Users} title="4. Contest Roles">
              <P>
                Entrants may compete in one or more of the following Roles:
              </P>
              <Ul items={ROLES} />
              <P>
                The first five Roles are collectively referred to as the Girl
                Group Roles.
              </P>
              <P>
                The Animal Mascot Role is a bonus category and is judged
                independently from the Girl Group Roles.
              </P>
              <P>
                An Entrant may participate in multiple Roles throughout the
                Contest.
              </P>
              <P>
                However, if an Entrant qualifies for the Final Mission in more
                than one Role, they must choose a single Role before
                participating in the Final Mission.
              </P>
              <P>
                Once a Role has been selected for the Final Mission, the Entrant
                is no longer eligible to compete in any other Role.
              </P>
            </Section>

            <Section icon={Upload} title="5. How to Enter">
              <P>To enter the Contest:</P>
              <Ul
                items={[
                  "Download or open the Pagent app.",
                  "Create or sign into your Pagent account.",
                  "Select the Role you wish to compete in.",
                  "Complete the current Mission.",
                  "Upload your submission before the applicable deadline.",
                ]}
              />
              <P>
                Mission requirements will be announced inside the Pagent app when
                each Mission begins.
              </P>
              <P>
                Mission prompts may differ depending on the Role being competed
                for. Examples include singing performances, dance performances,
                rap performances, personality challenges, makeup or styling
                challenges, collaborative performances, or other creative
                challenges.
              </P>
              <P>
                Animal Mascot submissions must feature an animal that the
                Entrant has the legal authority to photograph or record.
              </P>
              <P>
                Pagent may establish reasonable submission requirements,
                including time limits, technical specifications, and community
                guidelines.
              </P>
              <P>
                Incomplete, fraudulent, offensive, infringing, or late
                submissions may be rejected or removed.
              </P>
            </Section>

            <Section icon={Layers} title="6. Contest Format">
              <P>The Contest consists of five (5) Missions.</P>
              <P>Mission 1 is open to all eligible Entrants.</P>
              <P>
                Following each Mission, only a portion of Entrants will advance.
              </P>
              <P>
                Advancement is determined solely through valid votes received
                during the applicable Mission.
              </P>
              <P>
                Before voting opens for each Mission, Pagent will announce the
                advancement threshold.
              </P>
              <P>The advancement threshold may be expressed as:</P>
              <Ul
                items={[
                  "a percentage of Entrants;",
                  "a fixed number of Entrants; or",
                  "another objective voting-based threshold announced before voting begins.",
                ]}
              />
              <P>
                Once voting begins for a Mission, Pagent will not materially
                change the announced advancement threshold except where
                reasonably necessary to preserve the fairness or integrity of
                the Contest.
              </P>
              <P>Mission formats may vary.</P>
              <P>
                Some Missions may involve individual performances, while others
                may involve collaborative, duet, or group challenges.
              </P>
              <P>
                Unless Pagent expressly announces otherwise before a Mission
                begins, advancement continues to be determined on an individual
                basis using the applicable voting mechanics.
              </P>
              <P>
                Entrants who advance must complete each subsequent Mission
                within the announced submission period.
              </P>
              <P>
                Failure to submit a required Mission before the applicable
                deadline may result in elimination from the Contest.
              </P>
            </Section>

            <PartHeader
              part="Part III"
              title="Voting, Advancement & Winners"
              intro="Every Mission brings Entrants one step closer to becoming part of the Pagent Girl Group. This Part explains how voting works, how Entrants advance through the Contest, how Finalists are confirmed, and how Winners are selected."
            />

            <Section icon={Vote} title="7. Voting">
              <P>The Contest is powered by the Pagent community.</P>
              <P>
                Advancement through each Mission, and the ultimate selection of
                Winners, is determined by votes cast by Pagent users through the
                Pagent app.
              </P>
              <P>
                There are no judges and no random draws used to determine who
                advances or wins.
              </P>
              <P>
                Each Mission will have its own voting period, which will be
                announced through the Pagent app before voting begins.
              </P>
              <P>
                Pagent may establish voting rules for each Mission, including:
              </P>
              <Ul
                items={[
                  "how long voting remains open;",
                  "how many votes each user may cast;",
                  "whether votes reset between Missions;",
                  "voting eligibility requirements;",
                  "and any other voting mechanics necessary to administer the Contest.",
                ]}
              />
              <P>
                Unless otherwise announced, Entrants may vote for their own
                submissions.
              </P>
              <P>
                During each Mission, Pagent may display live vote counts,
                rankings, leaderboards, or similar information within the Pagent
                app.
              </P>
              <P>
                Any live rankings displayed during a Mission are unofficial and
                remain subject to verification before results become final.
              </P>
            </Section>

            <Section icon={ArrowUpRight} title="8. Advancing Through the Contest">
              <P>
                Following the conclusion of each Mission, eligible Entrants who
                satisfy the announced advancement threshold will move on to the
                next Mission.
              </P>
              <P>Before each Mission begins, Pagent will announce:</P>
              <Ul
                items={[
                  "the applicable advancement threshold;",
                  "the Mission timeline;",
                  "submission requirements;",
                  "voting dates; and",
                  "any Mission-specific rules.",
                ]}
              />
              <P>
                Advancement thresholds may differ between Missions depending on:
              </P>
              <Ul
                items={[
                  "the number of Entrants;",
                  "participation levels;",
                  "Contest structure; or",
                  "other operational considerations.",
                ]}
              />
              <P>
                Once voting for a Mission has begun, Pagent will not materially
                change that Mission&apos;s announced advancement threshold unless
                reasonably necessary to preserve the fairness or integrity of
                the Contest.
              </P>
              <P>
                Entrants who advance must complete the next Mission before the
                applicable deadline.
              </P>
              <P>Failure to do so may result in elimination.</P>
            </Section>

            <Section icon={ShieldCheck} title="9. Finalists">
              <P>
                Entrants who successfully advance through Mission 4 become
                Finalists.
              </P>
              <P>
                Before participating in Mission 5, each Finalist must complete
                Pagent&apos;s Finalist Verification process.
              </P>
              <P>
                This process helps ensure the integrity of the Contest and
                confirms that each Finalist remains eligible.
              </P>
              <P>
                As part of Finalist Verification, Pagent may require a Finalist
                to provide information reasonably necessary to confirm:
              </P>
              <Ul
                items={[
                  "identity;",
                  "age;",
                  "jurisdiction of residence;",
                  "eligibility;",
                  "compliance with this Rulebook;",
                  "and any other information reasonably required to administer the Contest.",
                ]}
              />
              <h3 className="pt-2 text-lg font-semibold text-white">
                Finalists Under the Age of Majority
              </h3>
              <P>
                Finalists who have not reached the age of majority in their
                jurisdiction must complete an additional parental or legal
                guardian verification process before participating in the Final
                Mission.
              </P>
              <P>
                Pagent may require a parent or legal guardian to:
              </P>
              <Ul
                items={[
                  "confirm the Finalist's participation;",
                  "confirm that they are the Finalist's parent or legal guardian;",
                  "consent to the Finalist's continued participation in the Contest;",
                  "acknowledge this Rulebook; and",
                  "provide any additional information reasonably required by Pagent.",
                ]}
              />
              <P>
                Failure to complete Finalist Verification within the required
                timeframe may result in disqualification.
              </P>
              <P>
                If a Finalist is disqualified or otherwise becomes ineligible
                before the Final Mission, Pagent may invite the next
                highest-ranked eligible Entrant from the applicable Role to
                participate.
              </P>
            </Section>

            <Section icon={Trophy} title="10. The Final Mission">
              <P>Mission 5 is the Final Mission of the Contest.</P>
              <P>
                Before participating in the Final Mission, any Finalist who has
                qualified in more than one Role must select a single Role in
                which to compete.
              </P>
              <P>Once that selection is made, it is final.</P>
              <P>
                The Final Mission may consist of one or more creative challenges
                determined by Pagent.
              </P>
              <P>
                Mission requirements will be announced before the Final Mission
                begins.
              </P>
              <P>
                Voting for the Final Mission will take place through the Pagent
                app using the announced voting mechanics.
              </P>
            </Section>

            <Section icon={Scale} title="11. Ties">
              <P>
                Pagent intends to structure each Mission to minimize the
                possibility of ties.
              </P>
              <P>
                If a tie nevertheless occurs that affects advancement or
                determines a Winner, Pagent may conduct an additional tiebreaker
                Mission or voting round.
              </P>
              <P>
                The format, timing, and voting rules applicable to any
                tiebreaker will be announced before it begins.
              </P>
              <P>
                If additional ties occur, Pagent may conduct further tiebreaker
                rounds until the tie has been resolved.
              </P>
            </Section>

            <Section icon={Star} title="12. Winner Selection">
              <P>
                Following the conclusion of the Final Mission, the eligible
                Finalist receiving the highest number of valid votes for each
                Girl Group Role will be declared the Winner of that Role.
              </P>
              <P>There will be six (6) Winners:</P>
              <Ul
                items={[
                  "one Vocalist;",
                  "one Rapper;",
                  "one Dancer;",
                  "one Visual;",
                  "one Leader & Personality; and",
                  "one Animal Mascot.",
                ]}
              />
              <P>
                The Winners of the five Girl Group Roles will collectively form
                the Pagent Girl Group.
              </P>
              <P>
                The Winner of the Animal Mascot Role will receive the title of
                Pagent Girl Group Mascot.
              </P>
              <P>
                Before being officially confirmed, each potential Winner must
                successfully complete Winner Confirmation.
              </P>
            </Section>

            <Section icon={BadgeCheck} title="13. Winner Confirmation">
              <P>
                Before a Winner is officially announced, Pagent may verify:
              </P>
              <Ul
                items={[
                  "eligibility;",
                  "compliance with this Rulebook;",
                  "the validity of voting results;",
                  "Finalist Verification information;",
                  "and any additional information reasonably necessary to administer the Contest.",
                ]}
              />
              <P>
                Pagent may delay announcing Winners until this process has been
                completed.
              </P>
            </Section>

            <Section icon={RefreshCw} title="14. If a Winner Cannot Accept">
              <P>If a potential Winner:</P>
              <Ul
                items={[
                  "declines their Role;",
                  "declines their prize;",
                  "fails to complete Winner Confirmation;",
                  "fails to complete required parental verification;",
                  "is disqualified;",
                  "or is otherwise unable or unwilling to accept the applicable Role,",
                ]}
              />
              <P>
                Pagent may declare the next highest-ranked eligible Finalist for
                that Role to be the Winner.
              </P>
              <P>
                This process may be repeated until a Winner is confirmed.
              </P>
            </Section>

            <Section icon={Gift} title="15. Prizes">
              <P>Each confirmed Winner will receive:</P>
              <Ul
                items={[
                  "a guaranteed minimum cash prize of USD $100;",
                  "official recognition on the Pagent platform;",
                  "the title associated with their winning Role; and",
                  "recognition as a member of the Pagent Girl Group or Pagent Girl Group Mascot, as applicable.",
                ]}
              />
              <P>
                Pagent may increase the value of prizes or introduce additional
                prizes during the Contest.
              </P>
              <P>
                Any such additions will be announced through official Pagent
                communication channels.
              </P>
              <P>
                Unless otherwise stated, Winners are responsible for any taxes
                or other obligations associated with accepting a prize.
              </P>
              <P>
                Prizes are not transferable except with Pagent&apos;s written
                approval.
              </P>
            </Section>

            <Section icon={Users} title="16. The Pagent Girl Group">
              <P>
                The Winners of the five Girl Group Roles will collectively
                receive the title of the Pagent Girl Group.
              </P>
              <P>Winning a Girl Group Role guarantees:</P>
              <Ul
                items={[
                  "the applicable title;",
                  "official recognition;",
                  "and the prizes described in this Rulebook.",
                ]}
              />
              <P>
                As a growing company, Pagent cannot guarantee the scale of any
                additional opportunities that may follow, such as:
              </P>
              <Ul
                items={[
                  "employment;",
                  "management representation;",
                  "a recording contract;",
                  "a publishing agreement;",
                  "commercial sponsorship;",
                  "or any other entertainment opportunity.",
                ]}
              />
              <P>
                The Pagent Girl Group designation is intended to recognize the
                Winners of the Contest.
              </P>
              <P>
                Whether that recognition later develops into additional
                opportunities depends on many factors outside the Contest
                itself.
              </P>
            </Section>

            <Section icon={Sparkles} title="17. Future Opportunities">
              <P>
                Pagent hopes the Contest becomes the beginning of something
                bigger.
              </P>
              <P>
                If one or more Winners are interested in pursuing additional
                opportunities beyond the Contest, Pagent may explore
                opportunities within the music and entertainment industries.
              </P>
              <P>Those opportunities may include discussions with:</P>
              <Ul
                items={[
                  "record labels;",
                  "producers;",
                  "managers;",
                  "distributors;",
                  "commercial partners;",
                  "sponsors;",
                  "or other industry professionals.",
                ]}
              />
              <P>
                These opportunities depend on many factors, including audience
                engagement, commercial interest, financing, the availability
                and interests of the Winners, and the willingness of third
                parties to participate.
              </P>
              <P>
                As a growing company, Pagent cannot guarantee the scale of any
                record deal, recording contract, management agreement,
                commercial representation, music release, performance
                opportunity, or other entertainment opportunity, but is
                committed to pursuing them.
              </P>
              <P>
                Participation in any future opportunity will always be voluntary
                and, where applicable, subject to separate agreements and
                additional parental or legal guardian consent.
              </P>
            </Section>

            <PartHeader
              part="Part IV"
              title="Contest Administration"
              intro="Running a global Contest requires everyone to play by the same rules. This Part explains how submissions may be used, how Pagent protects the integrity of the Contest, and what happens if something goes wrong."
            />

            <Section icon={FileText} title="18. Ownership of Your Content">
              <P>Your content remains yours.</P>
              <P>
                By participating in the Contest, you do not transfer
                ownership of your videos, photographs, music, performances,
                captions, artwork, or other original content to Pagent.
              </P>
              <P>
                Nothing in this Rulebook assigns or transfers your intellectual
                property rights to Pagent.
              </P>
            </Section>

            <Section icon={BadgeCheck} title="19. Licence to Pagent">
              <P>
                In order to operate and promote the Contest, you grant
                Pagent a worldwide, non-exclusive, royalty-free, transferable,
                and sublicensable licence to use your Submission, together with
                your name, username, image, likeness, voice, and performance as
                incorporated in your Submission, for the following purposes:
              </P>
              <Ul
                items={[
                  "administering and operating the Contest;",
                  "displaying Submissions within the Pagent app;",
                  "enabling users to view, share, and vote on Submissions;",
                  "promoting the Contest;",
                  "promoting Pagent and future Pagent contests;",
                  "announcing Entrants, Finalists, and Winners;",
                  "creating Contest highlights, recaps, and promotional materials;",
                  "maintaining historical archives of the Contest; and",
                  "any other purpose reasonably related to the administration, promotion, or operation of the Contest or the Pagent platform.",
                ]}
              />
              <P>
                This licence begins when your Submission is uploaded and
                continues for so long as reasonably necessary for the purposes
                described above, including maintaining historical Contest
                archives and promotional materials relating to past
                Contests.
              </P>
              <P>
                Pagent may edit, crop, resize, subtitle, caption, format,
                compress, or otherwise adapt your Submission as reasonably
                necessary for those purposes, provided the overall nature of
                your Submission is not materially altered.
              </P>
              <P>
                Except as expressly provided in this Rulebook, Pagent will not
                commercially exploit your Submission outside the Contest or
                the promotion of Pagent without your separate written agreement.
              </P>
              <P>
                For greater certainty, if Pagent wishes to use your Submission
                in connection with a television program, documentary, feature
                production, commercial advertising campaign unrelated to the
                Contest, merchandise, music release, or other standalone
                commercial project, that use will be subject to a separate
                agreement between you and Pagent.
              </P>
              <P>
                Nothing in this Rulebook grants Pagent ownership of your
                Submission or prevents you from using, licensing, or otherwise
                exploiting your own content.
              </P>
              <P>
                You represent and warrant that you own or control all rights
                necessary to grant the licence described in this Section.
              </P>
            </Section>

            <Section icon={UserCheck} title="20. Your Responsibilities">
              <P>
                By entering the Contest, you represent and warrant that:
              </P>
              <Ul
                items={[
                  "your Submission is your own original work, or you otherwise have all necessary rights and permissions to submit it;",
                  "your Submission does not infringe the intellectual property, privacy, publicity, contractual, or other rights of any third party;",
                  "your Submission complies with applicable laws, this Rulebook, the Pagent Terms of Service, and Pagent's Community Guidelines;",
                  "you have obtained any permissions required from individuals appearing in your Submission; and",
                  "all information you provide in connection with the Contest is true and accurate.",
                ]}
              />
              <P>
                If your Submission includes another person, you are responsible
                for ensuring you have obtained any permissions required for
                their appearance.
              </P>
              <P>
                If you submit content in the Animal Mascot Role, you confirm
                that you have the legal right to photograph, record, and submit
                that content.
              </P>
            </Section>

            <Section icon={Heart} title="21. Community Standards">
              <P>
                The Contest is intended to be fun, respectful, and
                welcoming.
              </P>
              <P>Entrants must not:</P>
              <Ul
                items={[
                  "manipulate votes;",
                  "create fake accounts;",
                  "use bots or automated voting;",
                  "purchase votes;",
                  "harass or intimidate other users;",
                  "submit unlawful or infringing content;",
                  "impersonate another person;",
                  "or otherwise interfere with the fair operation of the Contest.",
                ]}
              />
              <P>
                Pagent may investigate suspected misconduct at any time.
              </P>
            </Section>

            <Section icon={AlertTriangle} title="22. Disqualification">
              <P>
                Pagent may remove a submission or disqualify an Entrant where
                reasonably necessary to protect the integrity of the
                Contest.
              </P>
              <P>Examples include:</P>
              <Ul
                items={[
                  "fraudulent activity;",
                  "vote manipulation;",
                  "use of automated systems;",
                  "fake accounts;",
                  "repeated violations of Community Guidelines;",
                  "infringement of another person's rights;",
                  "abusive or dangerous conduct;",
                  "false eligibility information;",
                  "or other conduct that materially undermines the fairness or operation of the Contest.",
                ]}
              />
              <P>
                Where reasonably practicable, Pagent may request clarification
                or additional information before making a final decision.
              </P>
            </Section>

            <Section icon={WifiOff} title="23. Technical Issues">
              <P>
                While Pagent will make reasonable efforts to ensure the
                Contest operates smoothly, technical issues sometimes occur.
              </P>
              <P>Pagent is not responsible for interruptions caused by:</P>
              <Ul
                items={[
                  "internet failures;",
                  "software bugs;",
                  "device failures;",
                  "third-party service outages;",
                  "platform interruptions;",
                  "or circumstances beyond Pagent's reasonable control.",
                ]}
              />
              <P>
                If a technical issue materially affects the fairness of a
                Mission, Pagent may:
              </P>
              <Ul
                items={[
                  "extend a submission period;",
                  "extend voting;",
                  "repeat a Mission;",
                  "remove invalid votes;",
                  "pause the Contest;",
                  "or implement another reasonable solution designed to preserve fairness.",
                ]}
              />
            </Section>

            <Section icon={RefreshCw} title="24. Changes to the Contest">
              <P>
                As the Contest develops, adjustments may occasionally become
                necessary.
              </P>
              <P>Pagent may make reasonable changes to:</P>
              <Ul
                items={[
                  "Mission dates;",
                  "Contest timing;",
                  "submission requirements;",
                  "voting mechanics;",
                  "advancement thresholds for future Missions;",
                  "or other administrative details,",
                ]}
              />
              <P>
                provided those changes are made to preserve the fairness,
                integrity, or practical operation of the Contest.
              </P>
              <P>
                Pagent will not reduce the guaranteed minimum cash prizes after
                the Contest begins.
              </P>
              <P>
                Material changes will be communicated through the Pagent app or
                other official communication channels.
              </P>
            </Section>

            <Section icon={Lock} title="25. Privacy">
              <P>
                Personal information collected in connection with the Contest
                will be collected, used, stored, and disclosed in accordance
                with Pagent&apos;s{" "}
                <Link to="/privacy" className="text-yellow-400 hover:underline">
                  Privacy Policy
                </Link>{" "}
                and applicable law.
              </P>
              <P>Pagent may use personal information to:</P>
              <Ul
                items={[
                  "administer the Contest;",
                  "verify eligibility;",
                  "communicate with Entrants;",
                  "communicate with parents or legal guardians where required;",
                  "complete Finalist Verification and Winner Confirmation;",
                  "deliver prizes;",
                  "comply with applicable legal and regulatory obligations; and",
                  "investigate suspected fraud or misconduct.",
                ]}
              />
              <P>
                Where reasonably necessary, Pagent may require Finalists or
                Winners to provide additional information or documentation to
                verify identity, age, jurisdiction of residence, tax
                obligations, or other information required to administer the
                Contest or comply with applicable law.
              </P>
              <P>
                Information collected during Finalist Verification or Winner
                Confirmation will be used only for those purposes unless
                otherwise permitted by law.
              </P>
            </Section>

            <PartHeader
              part="Part V"
              title="Legal Information"
              intro="These final provisions help ensure the Contest is administered fairly and consistently."
            />

            <Section icon={Gavel} title="26. Release of Liability">
              <P>
                To the fullest extent permitted by law, Entrants participate in
                the Contest at their own risk.
              </P>
              <P>
                Pagent and its directors, officers, employees, contractors, and
                representatives are not responsible for losses or damages
                arising from:
              </P>
              <Ul
                items={[
                  "participation in the Contest;",
                  "inability to participate;",
                  "technical failures;",
                  "unauthorized interference with the Contest;",
                  "or acceptance or use of a prize.",
                ]}
              />
              <P>
                Nothing in this Rulebook limits any rights that cannot legally
                be limited under applicable law.
              </P>
            </Section>

            <Section icon={ShieldCheck} title="27. Indemnity">
              <P>
                Entrants agree to be responsible for losses arising from their
                own unlawful conduct, infringement of another person&apos;s
                rights, or breach of this Rulebook.
              </P>
              <P>
                Where permitted by law, Entrants agree to indemnify Pagent
                against third-party claims arising from those actions.
              </P>
              <P>
                For Entrants under the age of majority, this Section applies
                only to the extent permitted by applicable law.
              </P>
            </Section>

            <Section icon={Scale} title="28. Contest Decisions">
              <P>
                Pagent will administer the Contest fairly, consistently, and
                in accordance with this Rulebook.
              </P>
              <P>
                Questions involving eligibility, voting integrity, interpretation
                of this Rulebook, Contest administration, or technical
                issues will be decided by Pagent acting reasonably and in good
                faith.
              </P>
            </Section>

            <Section icon={Gavel} title="29. Governing Law">
              <P>
                This Rulebook is governed by the laws of the Province of Ontario
                and the federal laws of Canada applicable therein.
              </P>
              <P>
                Where permitted by applicable law, disputes relating to the
                Contest will be resolved by arbitration in Toronto, Ontario.
              </P>
              <P>
                Nothing in this Rulebook limits any mandatory rights available
                under applicable consumer protection or other applicable
                legislation.
              </P>
            </Section>

            <Section icon={Mail} title="30. Contact">
              <P>Questions about the Contest may be directed to:</P>
              <P>
                <strong className="text-white">Pagent Social Corp.</strong>
              </P>
              <P>
                Email:{" "}
                <a
                  href="mailto:support@pagent.tv"
                  className="text-yellow-400 hover:underline"
                >
                  support@pagent.tv
                </a>
              </P>
              <P>
                Website:{" "}
                <a
                  href="https://www.pagent.tv"
                  className="text-yellow-400 hover:underline"
                >
                  www.pagent.tv
                </a>
              </P>
            </Section>

            <Section icon={Heart} title="Thank You">
              <P>
                Whether you&apos;re here to sing, dance, rap, lead, show off
                your creativity, or introduce the world&apos;s next famous
                mascot...
              </P>
              <P>Thank you for being part of the Pagent community.</P>
              <P>
                Good luck, have fun, and we&apos;ll see you in the Contest.
              </P>
            </Section>
          </div>
        </div>
      </LegalShell>
    </>
  );
}
